import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { COMPANY } from '../constants/company'

export default function Privacidade() {
  return (
    <div className="min-h-screen bg-white text-black">
      <SEO
        title="Política de Privacidade"
        description="Política de Privacidade da WaitHere. Saiba como coletamos, usamos e protegemos seus dados pessoais."
        path="/privacidade"
      />
      <nav className="sticky top-0 z-50 bg-white/97 backdrop-blur-md border-b border-neutral-200">
        <div className="flex items-center justify-between px-5 py-4 md:px-10 lg:px-16">
          <Link to="/" className="no-underline">
            <img src="/waitherelogo.png" alt="Waithere" className="h-7 w-auto" />
          </Link>
          <Link
            to="/"
            className="text-sm text-neutral-500 no-underline hover:text-black transition-colors"
          >
            ← Voltar ao início
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-5 py-16 sm:px-8 lg:px-0">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Política de Privacidade</h1>
        <p className="text-neutral-400 text-sm mb-10">Última atualização: 29 de março de 2026</p>

        <div className="prose prose-neutral max-w-none space-y-6 text-[15px] leading-relaxed text-neutral-700">
          <section>
            <h2 className="text-xl font-semibold text-black mt-8 mb-3">1. Introdução</h2>
            <p>
              A {COMPANY.brandName} ("nós", "nosso" ou "nossa") opera a plataforma waithere.com.br (e wtr.app.br) e seus
              subdomínios. Esta Política de Privacidade descreve como coletamos, usamos, armazenamos
              e protegemos suas informações pessoais quando você utiliza nossos serviços.
            </p>
            <p>
              {COMPANY.brandName} é a marca comercial e plataforma operada pela {COMPANY.legalName}
              (CNPJ {COMPANY.cnpj}), responsável pelo tratamento dos dados pessoais descritos nesta política.
            </p>
            <p>
              Ao utilizar nossos serviços, você concorda com a coleta e o uso de informações de
              acordo com esta política.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-black mt-8 mb-3">2. Informações que coletamos</h2>
            <p>Podemos coletar os seguintes tipos de informações:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>Dados de cadastro:</strong> nome, e-mail, telefone e nome do estabelecimento
                ao criar uma conta.
              </li>
              <li>
                <strong>Dados de uso:</strong> informações sobre como você interage com nossa
                plataforma, incluindo páginas visitadas, funcionalidades utilizadas e horários de acesso.
              </li>
              <li>
                <strong>Dados de dispositivo:</strong> tipo de navegador, sistema operacional,
                endereço IP e identificadores de dispositivo.
              </li>
              <li>
                <strong>Cookies e tecnologias similares:</strong> utilizamos cookies para melhorar
                sua experiência, analisar tráfego e personalizar conteúdo.
              </li>
              <li>
                <strong>Dados de pagamento:</strong> informações de faturamento processadas por
                nossos parceiros de pagamento seguros. Não armazenamos dados completos de cartão
                de crédito em nossos servidores.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-black mt-8 mb-3">3. Como usamos suas informações</h2>
            <p>Utilizamos as informações coletadas para:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Fornecer, manter e melhorar nossos serviços.</li>
              <li>Processar transações e enviar notificações relacionadas.</li>
              <li>Enviar comunicações de marketing (com seu consentimento).</li>
              <li>Analisar tendências de uso para melhorar a experiência do usuário.</li>
              <li>Detectar, prevenir e resolver problemas técnicos e de segurança.</li>
              <li>Cumprir obrigações legais e regulatórias.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-black mt-8 mb-3">4. Compartilhamento de dados</h2>
            <p>
              Não vendemos suas informações pessoais. Podemos compartilhar dados com:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>Prestadores de serviço:</strong> empresas que nos auxiliam na operação da
                plataforma (hospedagem, pagamentos, análise de dados).
              </li>
              <li>
                <strong>Parceiros de publicidade:</strong> utilizamos serviços como Google AdSense
                para exibir anúncios. Esses parceiros podem usar cookies para personalizar anúncios
                com base em suas visitas.
              </li>
              <li>
                <strong>Obrigações legais:</strong> quando exigido por lei, processo judicial ou
                solicitação governamental.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-black mt-8 mb-3">5. Cookies e publicidade</h2>
            <p>
              Nosso site utiliza cookies para funcionalidades essenciais e para exibição de anúncios
              personalizados. Parceiros de publicidade, incluindo o Google, podem usar cookies para
              veicular anúncios com base em visitas anteriores ao nosso site ou a outros sites.
            </p>
            <p>
              Você pode desativar a publicidade personalizada visitando as{' '}
              <a
                href="https://www.google.com/settings/ads"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black underline hover:text-neutral-500"
              >
                Configurações de anúncios do Google
              </a>{' '}
              ou acessando{' '}
              <a
                href="https://www.aboutads.info/choices/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black underline hover:text-neutral-500"
              >
                aboutads.info
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-black mt-8 mb-3">6. Segurança dos dados</h2>
            <p>
              Empregamos medidas técnicas e organizacionais para proteger suas informações contra
              acesso não autorizado, alteração, divulgação ou destruição. Isso inclui criptografia
              de dados em trânsito (SSL/TLS) e em repouso, além de controles de acesso rigorosos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-black mt-8 mb-3">7. Seus direitos (LGPD)</h2>
            <p>
              De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem direito a:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Confirmar a existência de tratamento de seus dados.</li>
              <li>Acessar seus dados pessoais.</li>
              <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
              <li>Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários.</li>
              <li>Solicitar a portabilidade dos dados.</li>
              <li>Revogar seu consentimento a qualquer momento.</li>
            </ul>
            <p className="mt-3">
              Para exercer esses direitos, entre em contato conosco pelo e-mail{' '}
              <a href="mailto:contato@waithere.com.br" className="text-black underline hover:text-neutral-500">
                contato@waithere.com.br
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-black mt-8 mb-3">8. Retenção de dados</h2>
            <p>
              Mantemos suas informações pessoais pelo tempo necessário para cumprir as finalidades
              descritas nesta política, a menos que um período de retenção mais longo seja exigido
              ou permitido por lei.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-black mt-8 mb-3">9. Alterações nesta política</h2>
            <p>
              Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos sobre
              alterações significativas publicando a nova política nesta página e atualizando a
              data de "última atualização".
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-black mt-8 mb-3">10. Contato</h2>
            <p>
              Se tiver dúvidas sobre esta Política de Privacidade, entre em contato:
            </p>
            <ul className="list-none space-y-1 mt-3">
              <li><strong>Razão social:</strong> {COMPANY.legalName}</li>
              <li><strong>CNPJ:</strong> {COMPANY.cnpj}</li>
              <li><strong>E-mail:</strong> contato@waithere.com.br</li>
              <li>
                <strong>Redes sociais:</strong>{' '}
                <a
                  href="https://www.instagram.com/waithere.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black underline hover:text-neutral-500"
                >
                  @waithere.br
                </a>
              </li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  )
}
